/* De rijen die op de organisatie- en contactpagina terugkomen: contactpersonen,
 * projecten, offertes, locaties en Charging. Elke rij opent de eigen pagina. */

import { Link } from 'react-router-dom';
import { Mail, Phone } from 'lucide-react';
import { getStageConfig } from '@/components/crm/project/projectUtils';
import { cn } from '@/lib/utils';
import { euro } from '@/features/projectpagina/ui/opmaak';
import { StatusLabel } from '@/features/offertes/ui/StatusLabel';
import { lokaleDag, weergave } from '@/features/offertes/data/status';
import { siteStatusLabel } from '@/features/sites/data/labels';
import type { ContactKort, KansKort, KavelKort, LocatieKort, OfferteKort, ProjectKort } from '../data/pagina';
import { kansLabel, kavelLabel } from '../data/labels';
import { Rij, Rijen } from './Onderdelen';

const telLink = (t: string) => `tel:${t.replace(/[^\d+]/g, '')}`;

export function ContactRijen({ contacten, leeg }: { contacten: ContactKort[]; leeg: string }) {
  return (
    <Rijen leeg={leeg}>
      {contacten.map((c) => (
        <li key={c.id} data-contact={c.id} className="flex items-center">
          <Link to={`/crm/contacts/${c.id}`} className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm hover:bg-muted/60 focus-visible:bg-muted focus-visible:outline-none">
            <span className="min-w-0 flex-1">
              <span className="font-medium">{c.naam}</span>
              {c.functie && <span className="text-muted-foreground"> · {c.functie}</span>}
            </span>
            {c.email && <span className="hidden truncate text-muted-foreground md:inline">{c.email}</span>}
            {c.telefoon && <span className="hidden tabular-nums text-muted-foreground lg:inline">{c.telefoon}</span>}
          </Link>
          <span className="flex shrink-0 items-center gap-0.5 pr-2">
            {c.email && (
              <a href={`mailto:${c.email}`} aria-label={`${c.naam} mailen`} title={c.email} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground">
                <Mail className="h-4 w-4" />
              </a>
            )}
            {c.telefoon && (
              <a href={telLink(c.telefoon)} aria-label={`${c.naam} bellen`} title={c.telefoon} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground">
                <Phone className="h-4 w-4" />
              </a>
            )}
          </span>
        </li>
      ))}
    </Rijen>
  );
}

function Stadium({ stage }: { stage: string | null }) {
  const c = getStageConfig(stage);
  return <span className={cn('inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-medium', c.bg, c.text)}>{c.label}</span>;
}

export function ProjectRijen({ projecten, leeg }: { projecten: ProjectKort[]; leeg: string }) {
  return (
    <Rijen leeg={leeg}>
      {projecten.map((p) => (
        <Rij key={p.id} data-project={p.id} naar={`/crm/projects/${p.id}`}>
          <span className="w-12 shrink-0 font-mono text-xs text-muted-foreground">{p.nummer ?? '—'}</span>
          <span className="min-w-0 flex-1 truncate font-medium">
            {p.naam}
            {p.plaats && <span className="font-normal text-muted-foreground"> · {p.plaats}</span>}
          </span>
          {p.bedrag ? <span className="shrink-0 tabular-nums text-muted-foreground">{euro(p.bedrag)}</span> : null}
          <Stadium stage={p.stage} />
        </Rij>
      ))}
    </Rijen>
  );
}

export function OfferteRijen({ offertes, leeg }: { offertes: OfferteKort[]; leeg: string }) {
  const vandaag = lokaleDag();
  return (
    <Rijen leeg={leeg}>
      {offertes.map((q) => (
        <Rij key={q.id} data-offerte={q.id} naar={`/crm/quotes/${q.id}`}>
          <span className="w-16 shrink-0 font-mono text-xs text-muted-foreground">{q.nummer ?? '—'}</span>
          <span className="min-w-0 flex-1 truncate font-medium">{q.titel}</span>
          <span className="shrink-0 tabular-nums text-muted-foreground">{euro(q.bedrag)}</span>
          <StatusLabel weergave={weergave(q, vandaag)} />
        </Rij>
      ))}
    </Rijen>
  );
}

export function LocatieRijen({ locaties }: { locaties: LocatieKort[] }) {
  return (
    <Rijen leeg="Geen locaties.">
      {locaties.map((l) => (
        <Rij key={l.id} data-locatie={l.id} naar={`/crm/sites/${l.id}`}>
          <span className="w-12 shrink-0 font-mono text-xs text-muted-foreground">{l.nummer ?? '—'}</span>
          <span className="min-w-0 flex-1 truncate font-medium">{l.naam}</span>
          <span className="shrink-0 text-muted-foreground">{siteStatusLabel(l.status)}</span>
        </Rij>
      ))}
    </Rijen>
  );
}

export function ChargingRijen({ kansen, kavels }: { kansen: KansKort[]; kavels: KavelKort[] }) {
  return (
    <Rijen leeg="Niets in Charging.">
      {[
        ...kansen.map((k) => (
          <Rij key={`kans-${k.id}`} data-kans={k.id} naar={`/crm/charging/opportunities/${k.id}`}>
            <span className="w-20 shrink-0 text-xs text-muted-foreground">Opportunity</span>
            <span className="min-w-0 flex-1 truncate font-medium">
              {k.naam}
              {k.vloot ? (
                <span className="font-normal text-muted-foreground">
                  {' '}· {k.vloot} trucks{k.elektrisch ? `, ${k.elektrisch} elektrisch` : ''}
                </span>
              ) : null}
            </span>
            <span className="shrink-0 text-muted-foreground">{kansLabel(k.status)}</span>
          </Rij>
        )),
        ...kavels.map((k) => (
          <Rij key={`kavel-${k.id}`} data-kavel={k.id} naar={`/crm/charging/locatie/${k.id}`}>
            <span className="w-20 shrink-0 text-xs text-muted-foreground">Kavel</span>
            <span className="min-w-0 flex-1 truncate font-medium">
              {k.naam}
              {k.plaats && <span className="font-normal text-muted-foreground"> · {k.plaats}</span>}
            </span>
            <span className="shrink-0 text-muted-foreground">{kavelLabel(k.fase)}</span>
          </Rij>
        )),
      ]}
    </Rijen>
  );
}
